import { BodyText, Button, FormControl, Input, Link, Modal } from '@farol-ds/react'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { formatDate } from '@jusbrasil-web/shared-utils-format'
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import { useIMask } from 'react-imask'

import { INPUT_START_EVENT_PROPS, useGA4Events } from '../../../hooks'
import { BIRTHDATE_VALIDATIONS, CPF_VALIDATIONS, validateSpec } from '../../../utils'
import { PRIVACY_POLICY_URL, TERMS_URL } from '../constants'
import { useModalContext } from '../context'
import { Steps } from '../enums'
import styles from './steps.module.scss'

type FormErrorData = {
  cpf?: string
  birthDate?: string
}

const DEFAULT_TITLE = 'Verifique sua identidade'
const DEFAULT_DESCRIPTION = 'Preencha as seguintes informações para verificarmos a sua identidade.'

export function ValidateIdentity() {
  const formRef = useRef<HTMLFormElement>(null)

  const { setCurrentStep, setSharedData, sharedData, viewer, eventData, texts } = useModalContext()
  const pid = viewer?.pid.toString()
  const { cpf, birthDate } = viewer || {}

  const {
    ref: cpfRef,
    unmaskedValue: unmaskedCpf,
    maskRef,
  } = useIMask({
    mask: '000.000.000-00',
  })

  const { ref: birthDateRef } = useIMask({
    mask: '00/00/0000',
  })

  const gA4EventHandlers = useGA4Events()
  const hasTriggered = useRef(false)

  useEffect(() => {
    sendMetrics('UserAccount.IdValidationViewed', pid, { ...eventData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (maskRef.current) {
      maskRef.current.value = sharedData.cpf || cpf || ''
    }

    if (birthDateRef.current) {
      const defaultValue = sharedData.birthDate?.getTime() || birthDate
      const formattedDate = defaultValue ? formatDate(defaultValue) : ''
      birthDateRef.current.value = formattedDate
    }
  }, [maskRef, sharedData, birthDateRef, cpf, birthDate])

  const [errors, setErrors] = useState<FormErrorData>({})

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    setErrors({})

    const birthDate = birthDateRef.current?.value?.split('/').reverse().join('-')

    const cpfError = validateSpec(CPF_VALIDATIONS, unmaskedCpf)
    const birthDateError = validateSpec(BIRTHDATE_VALIDATIONS, birthDate || '')

    if (cpfError || birthDateError) {
      return setErrors({
        cpf: cpfError?.message,
        birthDate: birthDateError?.message,
      })
    }

    if (!birthDate || !unmaskedCpf) return

    const birthDateObject = new Date(`${birthDate}T00:00:00.000-03:00`)

    setSharedData({
      ...sharedData,
      cpf: unmaskedCpf,
      birthDate: birthDateObject,
    })

    sendMetrics('UserAccount.IdValidationSubmitted', pid, {
      ...eventData,
      metadata: {
        ...eventData?.metadata,
        cpf: unmaskedCpf,
        birthDate: formatDate(birthDateObject.getTime()),
      },
    })

    setCurrentStep(Steps.VALIDATING_IDENTITY)
  }

  const requestSubmit = () => formRef.current?.requestSubmit()

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!hasTriggered.current && e.target.value) {
      changeData()
      hasTriggered.current = true
    }
  }
  const submitButtonRef = useRef<HTMLButtonElement>(null)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return

    e.preventDefault()

    submitButtonRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })

    submitButtonRef.current?.focus()
  }

  const changeData = () => {
    gA4EventHandlers.sendInputStart(INPUT_START_EVENT_PROPS.VALIDATION_IDENTITY_FORM)
  }

  const title = texts?.validateIdentity?.title ?? DEFAULT_TITLE
  const description =
    texts?.validateIdentity?.description !== undefined
      ? texts.validateIdentity.description
      : DEFAULT_DESCRIPTION
  const buttonText = texts?.validateIdentity?.buttonText ?? 'Verificar identidade'
  const illustration = texts?.validateIdentity?.illustration

  return (
    <>
      {!illustration && (
        <Modal.Header>
          <Modal.HeaderTitle>{title}</Modal.HeaderTitle>
          {description && (
            <Modal.HeaderDescription className={styles.headerDescription}>
              <p>{description}</p>
            </Modal.HeaderDescription>
          )}
        </Modal.Header>
      )}

      <Modal.Body data-testid="validate-identity-step">
        {illustration && (
          <div className={styles.illustratedHeader} data-testid="validate-identity-illustration">
            <div className={styles.illustratedHeaderIllustration}>{illustration}</div>
            <Modal.HeaderTitle>{title}</Modal.HeaderTitle>
            {description && (
              <Modal.HeaderDescription
                className={`${styles.headerDescription} ${styles.illustratedHeaderDescription}`}
              >
                <p>{description}</p>
              </Modal.HeaderDescription>
            )}
          </div>
        )}
        <form ref={formRef} className={styles.form} onSubmit={onSubmit} noValidate>
          <FormControl required error={!!errors.cpf}>
            <FormControl.Label>CPF</FormControl.Label>
            <Input name="cpf" inputMode="numeric" ref={cpfRef} onChange={handleChange} />
            <FormControl.ErrorMessage>{errors.cpf}</FormControl.ErrorMessage>
          </FormControl>

          <FormControl required error={!!errors.birthDate}>
            <FormControl.Label>Data de nascimento</FormControl.Label>
            <Input
              type="text"
              ref={birthDateRef}
              inputMode="numeric"
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
            <FormControl.ErrorMessage>{errors.birthDate}</FormControl.ErrorMessage>
          </FormControl>

          <BodyText size="md">
            Concordo com os{' '}
            <Link type="secondary" href={TERMS_URL} target="_blank">
              Termos de Uso
            </Link>{' '}
            e a{' '}
            <Link type="secondary" href={PRIVACY_POLICY_URL} target="_blank">
              Política de Privacidade
            </Link>{' '}
            do Jusbrasil.
          </BodyText>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button kind="primary" onClick={requestSubmit} ref={submitButtonRef}>
          {buttonText}
        </Button>
      </Modal.Footer>
    </>
  )
}
